import { Button, ButtonGroup, Modal } from "@datapay-by-datacom/design-system";
import { Heading } from "react-aria-components";
import type { MatchingCsvAction } from "../types/matching";
import type { BulkMatchActionSummary } from "../utils/matchingBulkActions";
import type { MatchingBulkScope } from "./MatchingBulkActionBar";
import styles from "./MatchingBulkActionConfirmModal.module.css";

export interface MatchingBulkActionConfirmModalProps {
  isOpen: boolean;
  action: MatchingCsvAction | null;
  scope: MatchingBulkScope | null;
  summary: BulkMatchActionSummary | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirms bulk MatchAction changes before rewriting the in-memory batch.
 * Separate from MatchingImportConfirmModal (replace / commit / override).
 */
export function MatchingBulkActionConfirmModal({
  isOpen,
  action,
  scope,
  summary,
  onConfirm,
  onCancel,
}: MatchingBulkActionConfirmModalProps) {
  const ready = action !== null && scope !== null && summary !== null;
  const rowLabel = summary?.changed === 1 ? "row" : "rows";
  const scopeLabel =
    scope === "all" ? "all rows in this file" : "the selected rows";
  const title =
    scope === "all"
      ? `Set all rows to ${action}?`
      : `Set selected rows to ${action}?`;

  return (
    <Modal
      isOpen={isOpen && ready}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      {ready && (
        <div className={styles.modalContent}>
          <Heading slot="title" className={styles.title}>
            {title}
          </Heading>
          <p className={styles.message}>
            This will change MatchAction on {scopeLabel} without replacing the
            CSV. Validation will run again automatically.
          </p>
          <ul className={styles.summary}>
            <li className={`${styles.summaryItem} ${styles.itemNeutral}`}>
              <span className={styles.itemCount}>{summary.changed}</span>
              <span className={styles.itemLabel}>
                {rowLabel} will change to {action}
              </span>
            </li>
            {summary.amountCleared > 0 && (
              <li className={`${styles.summaryItem} ${styles.itemWarning}`}>
                <span className={styles.itemCount}>
                  {summary.amountCleared}
                </span>
                <span className={styles.itemLabel}>
                  TargetAmount value
                  {summary.amountCleared === 1 ? "" : "s"} will be cleared
                </span>
              </li>
            )}
            {summary.missingAmount > 0 && (
              <li className={`${styles.summaryItem} ${styles.itemWarning}`}>
                <span className={styles.itemCount}>
                  {summary.missingAmount}
                </span>
                <span className={styles.itemLabel}>
                  {summary.missingAmount === 1 ? "row" : "rows"} will need a
                  TargetAmount before import
                </span>
              </li>
            )}
          </ul>
          <div className={styles.actions}>
            <ButtonGroup>
              <Button variant="secondary" onPress={onCancel}>
                Cancel
              </Button>
              <Button variant="primary" onPress={onConfirm}>
                Change MatchAction
              </Button>
            </ButtonGroup>
          </div>
        </div>
      )}
    </Modal>
  );
}
